import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect, useState, Suspense, lazy } from 'react'
import MainLayout from '@components/layouts/MainLayout'
import authService from '@services/authService'
import logger from '@services/loggerService'

// Lazy-loaded pages for route-level code splitting
const Home = lazy(() => import('@pages/Home'))
const About = lazy(() => import('@pages/About'))
const Workshops = lazy(() => import('@pages/Workshops'))
const WorkshopDetail = lazy(() => import('@pages/WorkshopDetail'))
const Pricing = lazy(() => import('@pages/Pricing'))
const Contact = lazy(() => import('@pages/Contact'))
const Login = lazy(() => import('@pages/Login'))
const Signup = lazy(() => import('@pages/Signup'))
const ParentPortal = lazy(() => import('@pages/ParentPortal'))
const Communities = lazy(() => import('@pages/Communities'))
const CommunityDetail = lazy(() => import('@pages/CommunityDetail'))
// const CommunityDetailStream = lazy(() => import('@pages/CommunityDetailStream'))
const CommunityAdmin = lazy(() => import('@pages/CommunityAdmin'))
const AdminManagement = lazy(() => import('@pages/AdminManagement'))
const AdminUsers = lazy(() => import('@pages/AdminUsers'))
const NotFound = lazy(() => import('@pages/NotFound'))
const Team = lazy(() => import('@pages/Team'))
const MPower = lazy(() => import('@pages/MPower'))
const Homeschooling = lazy(() => import('@pages/Homeschooling'))
const SoranAcademy = lazy(() => import('@pages/SoranAcademy'))
const Programs = lazy(() => import('@pages/Programs'))
const Terms = lazy(() => import('@pages/Terms'))
const Privacy = lazy(() => import('@pages/Privacy'))
const AuthCallback = lazy(() => import('@pages/AuthCallback'))
const AdminWorkshops = lazy(() => import('@pages/AdminWorkshops'))
const Assessment = lazy(() => import('@pages/Assessment'))
const Onboarding = lazy(() => import('@pages/Onboarding'))
const OnboardingDemo = lazy(() => import('@pages/OnboardingDemo'))
const LaserAssessment = lazy(() => import('@pages/LaserAssessment'))
const EditParentProfile = lazy(() => import('@pages/EditParentProfile'))
const EditChildProfile = lazy(() => import('@pages/EditChildProfile'))
const BookConsultation = lazy(() => import('@pages/BookConsultation'))
// const Messages = lazy(() => import('@pages/Messages'))

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  
  useEffect(() => {
    // Check authentication status on app load
    const checkAuth = async () => {
      setIsLoading(true)
      try {
        // Check Firebase session first
        const { data: { session } } = await authService.supabase.auth.getSession()
        
        if (session) {
          // Update local storage and state
          localStorage.setItem('authToken', session.access_token)
          if (session.refresh_token) {
            localStorage.setItem('refreshToken', session.refresh_token)
          }
          
          // Get user profile if available
          try {
            const profile = await authService.getProfile(session.user.id)
            const fullUser = {
              id: session.user.id,
              email: session.user.email || '',
              firstName: profile?.firstName || session.user.user_metadata?.first_name,
              lastName: profile?.lastName || session.user.user_metadata?.last_name,
              avatarUrl: profile?.avatarUrl || session.user.user_metadata?.avatar_url,
              role: session.user.role,
            }
            localStorage.setItem('user', JSON.stringify(fullUser))
          } catch (profileError) {
            logger.info('Profile not found, using basic user data')
            const basicUser = {
              id: session.user.id,
              email: session.user.email || '',
              firstName: session.user.user_metadata?.first_name,
              lastName: session.user.user_metadata?.last_name,
              avatarUrl: session.user.user_metadata?.avatar_url,
              role: session.user.role,
            }
            localStorage.setItem('user', JSON.stringify(basicUser))
          }
          
          setIsAuthenticated(true)
        } else {
          // Fallback to local storage check
          const isAuth = authService.isAuthenticated()
          setIsAuthenticated(isAuth)
        }
      } catch (error) {
        logger.error('Error checking auth', {}, error as Error)
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }
    
    checkAuth()
    
    // Listen for auth state changes
    const { data: { subscription } } = authService.supabase.auth.onAuthStateChange(
      async (event, session) => {
        logger.info('Auth state changed', { event, email: session?.user?.email })
        
        if (event === 'SIGNED_IN' && session) {
          setIsAuthenticated(true)
          // Session data will be saved by AuthCallback component
        } else if (event === 'SIGNED_OUT') {
          setIsAuthenticated(false)
          localStorage.removeItem('authToken')
          localStorage.removeItem('refreshToken')
          localStorage.removeItem('user')
        }
      }
    )
    
    // Listen for storage events (for logout in other tabs)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        const isAuth = authService.isAuthenticated()
        setIsAuthenticated(isAuth)
      }
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      subscription.unsubscribe()
    }
  }, [])

 // Setup scroll animations - MOVED HERE TO FIX HOOK ORDER
 useEffect(() => {
   const setupScrollAnimations = () => {
     const observer = new IntersectionObserver((entries) => {
       entries.forEach(entry => {
         if (entry.isIntersecting) {
           entry.target.classList.add('active')
         }
       })
     }, { threshold: 0.1 })

     document.querySelectorAll('.reveal').forEach(element => {
       observer.observe(element)
     })

     return observer
   }

   const observer = setupScrollAnimations()

   return () => {
     if (observer) {
       document.querySelectorAll('.reveal').forEach(element => {
         observer.unobserve(element)
       })
     }
   }
 }, [])
 
 // Show loading screen while checking authentication
 if (isLoading) {
   return (
     <div className="min-h-screen flex items-center justify-center bg-gray-50">
       <div className="text-center">
         <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
         <p className="mt-4 text-gray-600">Loading...</p>
       </div>
     </div>
   )
 }
 
 // Protected route component
 const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
   if (!isAuthenticated) {
     return <Navigate to="/login" replace />
   }
   
   return <>{children}</>
 }

 return (
   <>
     <Suspense fallback={<div />}>
       <Routes>
         <Route path="/" element={<MainLayout />}>
           <Route index element={<Home />} />
           <Route path="about" element={<About />} />
           <Route path="mpower" element={<MPower />} />
           <Route path="soran-academy" element={<SoranAcademy />} />
           <Route path="homeschooling" element={<Homeschooling />} />
           <Route path="programs" element={<Programs />} />
           <Route path="workshops" element={<Workshops />} />
           <Route path="workshops/:id" element={<WorkshopDetail />} />
           <Route path="pricing" element={<Pricing />} />
           <Route path="contact" element={<Contact />} />
           <Route path="communities" element={<Communities />} />
           <Route path="community/:id" element={<CommunityDetail />} />
           <Route path="laser" element={<LaserAssessment />} />
         <Route path="login" element={isAuthenticated ? <Navigate to="/parent-portal" /> : <Login />} />
         <Route path="signup" element={isAuthenticated ? <Navigate to="/parent-portal" /> : <Signup />} />
         <Route path="terms" element={<Terms />} />
         <Route path="privacy" element={<Privacy />} />
         <Route path="parent-portal" element={isAuthenticated ? <ParentPortal /> : <Navigate to="/login" />} />
         <Route path="portal/edit-parent" element={isAuthenticated ? <EditParentProfile /> : <Navigate to="/login" />} />
         <Route path="portal/edit-child" element={isAuthenticated ? <EditChildProfile /> : <Navigate to="/login" />} />
         {/* <Route path="messages" element={isAuthenticated ? <Messages /> : <Navigate to="/login" />} /> */}
         <Route path="admin/workshops" element={<AdminWorkshops />} />
         <Route path="admin/communities" element={<CommunityAdmin />} />
         <Route path="admin/manage-admins" element={<AdminManagement />} />
         <Route path="admin/users" element={<AdminUsers />} />
         <Route path="auth/callback" element={<AuthCallback />} />
         <Route path="assessment" element={<Assessment />} />
         <Route path="workshops/:id/assessment" element={<Assessment />} />
         {/* Public demo route */}
         <Route path="demo/onboarding" element={<OnboardingDemo />} />
         
         {/* Protected Consultation Booking within Main Layout */}
         <Route path="book-consultation" element={isAuthenticated ? <BookConsultation /> : <Navigate to="/login" />} />
         
         <Route path="*" element={<NotFound />} />
       </Route>
       {/* Onboarding route outside MainLayout for full-screen experience */}
       <Route path="/onboarding" element={isAuthenticated ? <Onboarding /> : <Navigate to="/login" />} />
     </Routes>
   </Suspense>
   </>
 )
}

export default App